import { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { EmailListInput } from './EmailListInput';
import { ConfirmDialog } from './ConfirmDialog';
import { api, ApiError } from '../lib/api';

export type CertTab = 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';

interface RenewalConfig {
  enabled: boolean;
  lead_days: number;
  interval_days: number;
  max_reminders: number;
  cc_emails: string;
  subject: string;
  body_text?: string;
  body_html: string;
  editor_mode?: 'text' | 'html';
}

interface RenewalEmailConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const SAMPLE_EQUIPMENT_TABLE_FORM10 = `
<div style="margin: 18px 0; overflow-x: auto; border: 1px solid #cbd5e1; border-radius: 8px; background: #ffffff;">
  <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
    <thead>
      <tr style="background: #f1f5f9; border-bottom: 2px solid #cbd5e1; font-size: 11px; text-transform: uppercase; color: #475569; letter-spacing: 0.05em;">
        <th style="padding: 10px 8px; text-align: center; width: 32px;">#</th>
        <th style="padding: 10px 10px;">Certificate No.</th>
        <th style="padding: 10px 10px;">Equipment / Asset</th>
        <th style="padding: 10px 10px;">Next Due Date</th>
        <th style="padding: 10px 10px; text-align: center; width: 95px;">Status</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">1</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/F10/0142<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;">Form 10</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">EOT Crane - 10.0 Tons<br/><span style="font-size:11px;color:#64748b;font-family:monospace;">Tag: CRN/BAY2/01</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">27-09-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">8d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
      <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">2</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/F10/0143<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;">Form 10</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Electric Wire Rope Hoist - 5.0 Tons<br/><span style="font-size:11px;color:#64748b;font-family:monospace;">Tag: HST/BAY1/04</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">27-09-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">8d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
      <tr style="font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">3</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/F10/0144<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#eff6ff;color:#1d4ed8;border:1px solid #bfdbfe;">Form 10</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Webbing Sling Set (Pair) - 3.0 Tons<br/><span style="font-size:11px;color:#64748b;font-family:monospace;">Tag: SLG/FAB/12</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">27-09-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">8d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
    </tbody>
  </table>
</div>
`;

const SAMPLE_EQUIPMENT_TABLE_FORM11 = `
<div style="margin: 18px 0; overflow-x: auto; border: 1px solid #cbd5e1; border-radius: 8px; background: #ffffff;">
  <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
    <thead>
      <tr style="background: #f1f5f9; border-bottom: 2px solid #cbd5e1; font-size: 11px; text-transform: uppercase; color: #475569; letter-spacing: 0.05em;">
        <th style="padding: 10px 8px; text-align: center; width: 32px;">#</th>
        <th style="padding: 10px 10px;">Certificate No.</th>
        <th style="padding: 10px 10px;">Equipment / Asset</th>
        <th style="padding: 10px 10px;">Next Due Date</th>
        <th style="padding: 10px 10px; text-align: center; width: 95px;">Status</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">1</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/F11/0089<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#f3e8ff;color:#6b21a8;border:1px solid #d8b4fe;">Form 11</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Air Receiver Tank (Horizontal, 2.5 m³)<br/><span style="font-size:11px;color:#64748b;font-family:monospace;">Tag: ART-408-2019</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">24-09-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">5d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
      <tr style="font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">2</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/F11/0090<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#f3e8ff;color:#6b21a8;border:1px solid #d8b4fe;">Form 11</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Buffer Tank - Utility Section (1.0 m³)<br/><span style="font-size:11px;color:#64748b;font-family:monospace;">Tag: BT-102-2021</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">24-09-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">5d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
    </tbody>
  </table>
</div>
`;

const SAMPLE_EQUIPMENT_TABLE_TRAINING = `
<div style="margin: 18px 0; overflow-x: auto; border: 1px solid #cbd5e1; border-radius: 8px; background: #ffffff;">
  <table style="width: 100%; border-collapse: collapse; text-align: left; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
    <thead>
      <tr style="background: #f1f5f9; border-bottom: 2px solid #cbd5e1; font-size: 11px; text-transform: uppercase; color: #475569; letter-spacing: 0.05em;">
        <th style="padding: 10px 8px; text-align: center; width: 32px;">#</th>
        <th style="padding: 10px 10px;">Certificate No.</th>
        <th style="padding: 10px 10px;">Trainee / Course</th>
        <th style="padding: 10px 10px;">Expiry Date</th>
        <th style="padding: 10px 10px; text-align: center; width: 95px;">Status</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom: 1px solid #e2e8f0; font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">1</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/TR/0210<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#ecfdf5;color:#047857;border:1px solid #a7f3d0;">Training</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Dharmesh V. Parmar<br/><span style="font-size:11px;color:#64748b;">Basic Fire Fighting & Evacuation</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">14-10-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">25d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
      <tr style="font-size: 13px;">
        <td style="padding: 10px 8px; text-align: center; color: #64748b;">2</td>
        <td style="padding: 10px 10px; font-family: monospace; font-weight: 600; color: #0f172a;">GNG/2026-27/TR/0211<div style="margin-top: 3px;"><span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:10px;font-weight:700;background:#ecfdf5;color:#047857;border:1px solid #a7f3d0;">Training</span></div></td>
        <td style="padding: 10px 10px; color: #1e293b; font-weight: 500;">Ramesh K. Baria<br/><span style="font-size:11px;color:#64748b;">Basic Fire Fighting & Evacuation</span></td>
        <td style="padding: 10px 10px; color: #334155; font-weight: 600;">14-10-2026<div style="font-size: 11px; color: #b45309; font-weight: 600; margin-top: 2px;">25d left</div></td>
        <td style="padding: 10px 10px; text-align: center;"><span style="display: inline-block; padding: 3px 8px; border-radius: 9999px; font-size: 11px; font-weight: 700; background: #fffbeb; color: #b45309; border: 1px solid #b4530933;">Expiring Soon</span></td>
      </tr>
    </tbody>
  </table>
</div>
`;

const FORM10_VARS = [
  {
    category: 'Inspection Batch / Group',
    items: [
      { tag: '{{examination_date}}', label: 'Batch Exam Date' },
      { tag: '{{certificates_count}}', label: 'Total Equipments Count' },
      { tag: '{{earliest_due_date}}', label: 'Earliest Due Date' },
      { tag: '{{days_until_due}}', label: 'Days Remaining' },
      { tag: '{{current_date}}', label: "Today's Date" },
      { tag: '{{portal_url}}', label: 'Portal Link' },
    ],
  },
  {
    category: 'Equipment Batch Table',
    items: [
      { tag: '{{equipment_table}}', label: 'Dynamic Equipment Table (HTML)' },
      { tag: '{{certificate_numbers_list}}', label: 'Cert Numbers List' },
    ],
  },
  {
    category: 'Company Details',
    items: [
      { tag: '{{company_name}}', label: 'Company Name' },
      { tag: '{{contact_person}}', label: 'Contact Person' },
      { tag: '{{occupier_name}}', label: 'Occupier Name' },
      { tag: '{{communication_email}}', label: 'Communication Email' },
    ],
  },
  {
    category: 'Single Equipment Fallback',
    items: [
      { tag: '{{certificate_number}}', label: 'Certificate No.' },
      { tag: '{{equipment_name}}', label: 'Equipment Name' },
      { tag: '{{equipment_short_code}}', label: 'Short Code' },
      { tag: '{{internal_id_number}}', label: 'Tag / Internal ID' },
      { tag: '{{safe_working_load}}', label: 'SWL Capacity' },
      { tag: '{{location_in_factory}}', label: 'Location' },
      { tag: '{{test_method}}', label: 'Test Method' },
    ],
  },
];

const FORM11_VARS = [
  {
    category: 'Inspection Batch / Group',
    items: [
      { tag: '{{examination_date}}', label: 'Batch Exam Date' },
      { tag: '{{certificates_count}}', label: 'Total Vessels Count' },
      { tag: '{{earliest_due_date}}', label: 'Earliest Due Date' },
      { tag: '{{days_until_due}}', label: 'Days Remaining' },
      { tag: '{{current_date}}', label: "Today's Date" },
      { tag: '{{portal_url}}', label: 'Portal Link' },
    ],
  },
  {
    category: 'Vessels Batch Table',
    items: [
      { tag: '{{equipment_table}}', label: 'Dynamic Vessels Table (HTML)' },
      { tag: '{{certificate_numbers_list}}', label: 'Cert Numbers List' },
    ],
  },
  {
    category: 'Company Details',
    items: [
      { tag: '{{company_name}}', label: 'Company Name' },
      { tag: '{{contact_person}}', label: 'Contact Person' },
      { tag: '{{occupier_name}}', label: 'Occupier Name' },
      { tag: '{{communication_email}}', label: 'Communication Email' },
    ],
  },
  {
    category: 'Single Vessel Fallback',
    items: [
      { tag: '{{certificate_number}}', label: 'Certificate No.' },
      { tag: '{{vessel_name_description}}', label: 'Vessel Description' },
      { tag: '{{vessel_serial_no}}', label: 'Vessel Serial No.' },
      { tag: '{{safe_working_pressure}}', label: 'Working Pressure' },
      { tag: '{{hydro_test_date}}', label: 'Hydro Test Due Date' },
    ],
  },
];

const TRAINING_VARS = [
  {
    category: 'Training Batch / Group',
    items: [
      { tag: '{{training_date}}', label: 'Batch Training Date' },
      { tag: '{{certificates_count}}', label: 'Total Candidates Count' },
      { tag: '{{earliest_due_date}}', label: 'Earliest Due Date' },
      { tag: '{{days_until_due}}', label: 'Days Remaining' },
      { tag: '{{current_date}}', label: "Today's Date" },
      { tag: '{{portal_url}}', label: 'Portal Link' },
    ],
  },
  {
    category: 'Candidates Batch Table',
    items: [
      { tag: '{{equipment_table}}', label: 'Dynamic Candidates Table (HTML)' },
      { tag: '{{certificate_numbers_list}}', label: 'Cert Numbers List' },
    ],
  },
  {
    category: 'Company Details',
    items: [
      { tag: '{{company_name}}', label: 'Company Name' },
      { tag: '{{contact_person}}', label: 'Contact Person' },
      { tag: '{{occupier_name}}', label: 'Occupier Name' },
      { tag: '{{communication_email}}', label: 'Communication Email' },
    ],
  },
  {
    category: 'Single Candidate Fallback',
    items: [
      { tag: '{{certificate_number}}', label: 'Certificate No.' },
      { tag: '{{trainee_name}}', label: 'Trainee Name' },
      { tag: '{{course_title}}', label: 'Course Title' },
      { tag: '{{validity_text}}', label: 'Validity Period' },
      { tag: '{{valid_upto}}', label: 'Expiry Date' },
    ],
  },
];

const MOCK_DATA_MAP: Record<CertTab, Record<string, string>> = {
  FORM_9: {
    '{{company_name}}': 'Schott Poonawalla Private Limited',
    '{{contact_person}}': 'Mr. Rajesh Patel',
    '{{occupier_name}}': 'Mr. Ashok S. Saxena',
    '{{communication_email}}': 'compliance@schott.com',
    '{{examination_date}}': '28-09-2025',
    '{{certificates_count}}': '3',
    '{{earliest_due_date}}': '27-09-2026',
    '{{days_until_due}}': '8',
    '{{current_date}}': new Date().toLocaleDateString('en-GB'),
    '{{portal_url}}': 'https://app.gngehs.com',
    '{{equipment_table}}': SAMPLE_EQUIPMENT_TABLE_FORM10.replace(/Form 10/g, 'Form 9'),
    '{{certificate_numbers_list}}': 'GNG/2026-27/F10/0142, GNG/2026-27/F10/0143, GNG/2026-27/F10/0144',
    '{{certificate_number}}': 'GNG/2026-27/F10/0142',
    '{{equipment_name}}': 'EOT Crane - 10.0 Tons',
    '{{equipment_short_code}}': 'EOT CRANE',
    '{{internal_id_number}}': 'SPPL/CRN/03',
    '{{safe_working_load}}': '10.0 Tons',
    '{{location_in_factory}}': 'Bay-2 Foundry Workshop',
    '{{test_method}}': 'Visual and thorough physical examination',
  },
  FORM_10: {
    '{{company_name}}': 'Schott Poonawalla Private Limited',
    '{{contact_person}}': 'Mr. Rajesh Patel',
    '{{occupier_name}}': 'Mr. Ashok S. Saxena',
    '{{communication_email}}': 'compliance@schott.com',
    '{{examination_date}}': '28-09-2025',
    '{{certificates_count}}': '3',
    '{{earliest_due_date}}': '27-09-2026',
    '{{days_until_due}}': '8',
    '{{current_date}}': new Date().toLocaleDateString('en-GB'),
    '{{portal_url}}': 'https://app.gngehs.com',
    '{{equipment_table}}': SAMPLE_EQUIPMENT_TABLE_FORM10,
    '{{certificate_numbers_list}}': 'GNG/2026-27/F10/0142, GNG/2026-27/F10/0143, GNG/2026-27/F10/0144',
    '{{certificate_number}}': 'GNG/2026-27/F10/0142',
    '{{equipment_name}}': 'EOT Crane - 10.0 Tons',
    '{{equipment_short_code}}': 'EOT CRANE',
    '{{internal_id_number}}': 'SPPL/CRN/03',
    '{{safe_working_load}}': '10.0 Tons',
    '{{location_in_factory}}': 'Bay-2 Foundry Workshop',
    '{{test_method}}': 'Visual and thorough physical examination',
  },
  FORM_11: {
    '{{company_name}}': 'Reliance Industries Limited',
    '{{contact_person}}': 'Mr. Amit Shah',
    '{{occupier_name}}': 'Mr. N. R. Meswani',
    '{{communication_email}}': 'ehs.plants@ril.com',
    '{{examination_date}}': '25-03-2026',
    '{{certificates_count}}': '2',
    '{{earliest_due_date}}': '24-09-2026',
    '{{days_until_due}}': '5',
    '{{current_date}}': new Date().toLocaleDateString('en-GB'),
    '{{portal_url}}': 'https://app.gngehs.com',
    '{{equipment_table}}': SAMPLE_EQUIPMENT_TABLE_FORM11,
    '{{certificate_numbers_list}}': 'GNG/2026-27/F11/0089, GNG/2026-27/F11/0090',
    '{{certificate_number}}': 'GNG/2026-27/F11/0089',
    '{{vessel_name_description}}': 'Air Receiver Tank (Horizontal, 2.5 m³)',
    '{{vessel_serial_no}}': 'ART-408-2019',
    '{{safe_working_pressure}}': '10.5 kg/cm²',
    '{{hydro_test_date}}': '15-11-2027',
  },
  TRAINING: {
    '{{company_name}}': 'Larsen & Toubro Limited',
    '{{contact_person}}': 'Mr. Haresh Dave',
    '{{occupier_name}}': 'Mr. S. N. Subrahmanyan',
    '{{communication_email}}': 'safety.training@larsentoubro.com',
    '{{training_date}}': '15-10-2025',
    '{{certificates_count}}': '2',
    '{{earliest_due_date}}': '14-10-2026',
    '{{days_until_due}}': '25',
    '{{current_date}}': new Date().toLocaleDateString('en-GB'),
    '{{portal_url}}': 'https://app.gngehs.com',
    '{{equipment_table}}': SAMPLE_EQUIPMENT_TABLE_TRAINING,
    '{{certificate_numbers_list}}': 'GNG/2026-27/TR/0210, GNG/2026-27/TR/0211',
    '{{certificate_number}}': 'GNG/2026-27/TR/0210',
    '{{trainee_name}}': 'Dharmesh V. Parmar',
    '{{course_title}}': 'Basic Fire Fighting & Emergency Evacuation',
    '{{validity_text}}': '01 YEAR',
    '{{valid_upto}}': '14-10-2026',
  },
};

export function RenewalEmailConfigModal({ open, onClose, onSaved }: RenewalEmailConfigModalProps) {
  const [activeTab, setActiveTab] = useState<CertTab>('TRAINING');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Template editor lock state (locked by default to prevent accidental edits)
  const [isTemplateUnlocked, setIsTemplateUnlocked] = useState(false);
  const [showConfirmEditModal, setShowConfirmEditModal] = useState(false);

  const [smtpServer, setSmtpServer] = useState<{ host: string; port: number; user: string }>({
    host: 'smtp.zoho.in',
    port: 465,
    user: 'renewels@gngehs.com',
  });

  const [configs, setConfigs] = useState<Record<CertTab, RenewalConfig>>({
    FORM_9: {
      enabled: false,
      lead_days: 30,
      interval_days: 7,
      max_reminders: 3,
      cc_emails: 'renewels@gngehs.com',
      subject: '',
      body_text: '',
      body_html: '',
      editor_mode: 'text',
    },
    FORM_10: {
      enabled: true,
      lead_days: 30,
      interval_days: 7,
      max_reminders: 3,
      cc_emails: 'renewels@gngehs.com',
      subject: '',
      body_text: '',
      body_html: '',
      editor_mode: 'text',
    },
    FORM_11: {
      enabled: true,
      lead_days: 30,
      interval_days: 7,
      max_reminders: 3,
      cc_emails: 'renewels@gngehs.com',
      subject: '',
      body_text: '',
      body_html: '',
      editor_mode: 'text',
    },
    TRAINING: {
      enabled: true,
      lead_days: 30,
      interval_days: 7,
      max_reminders: 3,
      cc_emails: 'renewels@gngehs.com',
      subject: '',
      body_text: '',
      body_html: '',
      editor_mode: 'text',
    },
  });

  // Track editor tab (Live Preview enabled by default)
  const [activeView, setActiveView] = useState<'editor' | 'preview'>('preview');
  // Track focused field for variable chip insertion ('subject' | 'body')
  const [focusedField, setFocusedField] = useState<'subject' | 'body'>('body');

  const subjectRef = useRef<HTMLInputElement | null>(null);
  const textBodyRef = useRef<HTMLTextAreaElement | null>(null);
  const htmlBodyRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    setSuccessMsg('');
    api.renewalEmails
      .getConfig()
      .then((res: any) => {
        if (res.data) {
          setConfigs({
            FORM_9: res.data.form9,
            FORM_10: res.data.form10,
            FORM_11: res.data.form11,
            TRAINING: res.data.training,
          });
          if (res.data.smtp) setSmtpServer(res.data.smtp);
        }
      })
      .catch((err: ApiError) => {
        setError(err.message || 'Failed to load email configuration');
      })
      .finally(() => setLoading(false));
  }, [open]);

  const currentConfig = configs[activeTab];

  const updateCurrentConfig = (updater: (prev: RenewalConfig) => RenewalConfig) => {
    setConfigs((prev) => ({
      ...prev,
      [activeTab]: updater(prev[activeTab]),
    }));
  };

  const handleInsertVariable = (tag: string) => {
    if (focusedField === 'subject') {
      const input = subjectRef.current;
      if (!input) {
        updateCurrentConfig((p) => ({ ...p, subject: p.subject + ' ' + tag }));
        return;
      }
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const current = currentConfig.subject;
      const updated = current.slice(0, start) + tag + current.slice(end);
      updateCurrentConfig((p) => ({ ...p, subject: updated }));
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + tag.length, start + tag.length);
      }, 50);
    } else {
      // Body insertion
      const isHtmlMode = currentConfig.editor_mode === 'html';
      const textarea = isHtmlMode ? htmlBodyRef.current : textBodyRef.current;
      if (!textarea) {
        if (isHtmlMode) {
          updateCurrentConfig((p) => ({ ...p, body_html: p.body_html + '\n' + tag }));
        } else {
          updateCurrentConfig((p) => ({ ...p, body_text: (p.body_text || '') + '\n' + tag }));
        }
        return;
      }

      const start = textarea.selectionStart || 0;
      const end = textarea.selectionEnd || 0;

      if (isHtmlMode) {
        const current = currentConfig.body_html || '';
        const updated = current.slice(0, start) + tag + current.slice(end);
        updateCurrentConfig((p) => ({ ...p, body_html: updated }));
      } else {
        const current = currentConfig.body_text || '';
        const updated = current.slice(0, start) + tag + current.slice(end);
        updateCurrentConfig((p) => ({ ...p, body_text: updated }));
      }

      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + tag.length, start + tag.length);
      }, 50);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      await api.renewalEmails.saveConfig({
        type: activeTab,
        config: currentConfig,
      });

      // Synchronize latest configuration state from server
      try {
        const freshRes: any = await api.renewalEmails.getConfig();
        if (freshRes?.data) {
          setConfigs({
            FORM_9: freshRes.data.form9,
            FORM_10: freshRes.data.form10,
            FORM_11: freshRes.data.form11,
            TRAINING: freshRes.data.training,
          });
          if (freshRes.data.smtp) setSmtpServer(freshRes.data.smtp);
        }
      } catch {
        // Non-blocking background re-sync
      }

      const tabName =
        activeTab === 'FORM_9' ? 'Hoists and Lifts (Form 9)' : activeTab === 'FORM_10'
          ? 'Form 10'
          : activeTab === 'FORM_11'
          ? 'Form 11'
          : 'Training Certificate';
      setSuccessMsg(`${tabName} email settings saved successfully!`);
      if (onSaved) onSaved();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestingSmtp(true);
    setError('');
    setSuccessMsg('');
    try {
      const latestCc = currentConfig.cc_emails?.trim() || '';
      const recipient = smtpServer.user || 'renewels@gngehs.com';
      const res: any = await api.renewalEmails.testSmtp({
        recipient,
        cc_emails: latestCc,
        type: activeTab,
      });
      setSuccessMsg(res.data?.message || res.message || 'SMTP connection verified successfully!');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'SMTP connection test failed');
    } finally {
      setTestingSmtp(false);
    }
  };

  const renderPreviewSubject = () => {
    const mock = MOCK_DATA_MAP[activeTab];
    let rendered = currentConfig.subject;
    for (const [tag, val] of Object.entries(mock)) {
      rendered = rendered.split(tag).join(val);
    }
    return rendered;
  };

  const renderPreviewBody = () => {
    const mock = MOCK_DATA_MAP[activeTab];
    if (currentConfig.editor_mode === 'html') {
      let rendered = currentConfig.body_html || '';
      for (const [tag, val] of Object.entries(mock)) {
        rendered = rendered.split(tag).join(val);
      }
      return rendered;
    }

    // Normal text mode: render text into HTML preview
    const rawText = currentConfig.body_text || '';
    let interpolatedText = rawText;
    for (const [tag, val] of Object.entries(mock)) {
      interpolatedText = interpolatedText.split(tag).join(val);
    }

    const title =
      activeTab === 'FORM_9' ? 'Hoists and Lifts (Form 9)' : activeTab === 'FORM_10'
        ? 'Lifting Equipment Statutory Examination (Form 10)'
        : activeTab === 'FORM_11'
        ? 'Pressure Vessel Statutory Examination (Form 11)'
        : 'Safety Training Certification (Worker/Operator)';

    const themeColor = activeTab === 'FORM_10' ? '#0284c7' : activeTab === 'FORM_11' ? '#7c3aed' : '#16a34a';

    const escapeHtml = (str: string) =>
      str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    const formattedBlocks = interpolatedText
      .split(/\n\s*\n/)
      .map((block) => {
        const trimmed = block.trim();
        if (
          trimmed.includes('<table') ||
          trimmed.startsWith('<div') ||
          trimmed.includes('{{equipment_table}}')
        ) {
          return trimmed;
        }

        const lines = block.split('\n');
        const isList = lines.some((l) => /^[•\-\*]\s+/.test(l.trim()));

        if (isList) {
          const listItems = lines
            .map((line) => {
              const itemTrimmed = line.trim();
              if (/^[•\-\*]\s+/.test(itemTrimmed)) {
                return `<li style="margin-bottom: 6px; color: #1e293b;">${escapeHtml(itemTrimmed.replace(/^[•\-\*]\s*/, ''))}</li>`;
              }
              return escapeHtml(line);
            })
            .join('');
          return `<ul style="margin: 0 0 16px; padding-left: 22px; font-size: 14px; line-height: 1.6;">${listItems}</ul>`;
        }

        return `<p style="margin: 0 0 16px; font-size: 14px; line-height: 1.6; color: #334155;">${lines.map(escapeHtml).join('<br/>')}</p>`;
      })
      .join('');

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: 'Inter', system-ui, -apple-system, sans-serif; line-height: 1.6; color: #1e293b; margin: 0; padding: 0; background-color: #f8fafc; }
  .container { max-width: 680px; margin: 24px auto; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
  .header { background: #0f172a; padding: 24px 32px; color: #ffffff; text-align: left; border-bottom: 3px solid ${themeColor}; }
  .header h1 { margin: 0; font-size: 18px; font-weight: 700; letter-spacing: -0.02em; }
  .header p { margin: 4px 0 0; font-size: 12px; color: #94a3b8; }
  .content { padding: 28px 32px; }
  .footer { background: #f8fafc; padding: 20px 32px; text-align: center; font-size: 12px; color: #94a3b8; border-top: 1px solid #e2e8f0; }
  .footer a { color: ${themeColor}; text-decoration: none; font-weight: 600; }
</style>
</head>
<body>
<div class="container">
  <div class="header">
    <h1>G&G EHS Compliance Services</h1>
    <p>${title}</p>
  </div>
  <div class="content">
    ${formattedBlocks}
  </div>
  <div class="footer">
    <p style="margin: 0 0 6px;"><strong>G&G Engineering &amp; Health Safety</strong></p>
    <p style="margin: 0;">Government Recognized Inspection &bull; Training &bull; Vadodara, Gujarat</p>
    <p style="margin: 6px 0 0;"><a href="mailto:renewels@gngehs.com">renewels@gngehs.com</a></p>
  </div>
</div>
</body>
</html>`;
  };

  const variableGroups =
    activeTab === 'FORM_10'
      ? FORM10_VARS
      : activeTab === 'FORM_11'
      ? FORM11_VARS
      : TRAINING_VARS;

  return (
    <Modal open={open} onClose={onClose} title="Renewal Email Configuration" size="full">
      <div className="flex flex-col h-[calc(100vh-130px)]">
        {/* Certificate Navigation Tabs (Order: Training, Form 10, Form 11) */}
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => { setActiveTab('TRAINING'); setIsTemplateUnlocked(false); }}
              className={`btn text-xs py-1.5 ${activeTab === 'TRAINING' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <span>🎓</span>
              <span>Training Certificates</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('FORM_9'); setIsTemplateUnlocked(false); }}
              className={`btn text-xs py-1.5 ${activeTab === 'FORM_9' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <span>📋</span>
              <span>Form 9</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('FORM_10'); setIsTemplateUnlocked(false); }}
              className={`btn text-xs py-1.5 ${activeTab === 'FORM_10' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <span>📋</span>
              <span>Form 10</span>
            </button>
            <button
              type="button"
              onClick={() => { setActiveTab('FORM_11'); setIsTemplateUnlocked(false); }}
              className={`btn text-xs py-1.5 ${activeTab === 'FORM_11' ? 'btn-primary' : 'btn-secondary'}`}
            >
              <span>🧪</span>
              <span>Form 11</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 text-xs bg-slate-50 border border-gray-200 px-3 py-1.5 rounded-lg text-slate-600">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block animate-pulse" />
              <span>
                Server: <strong>{smtpServer.host}</strong> ({smtpServer.user})
              </span>
            </div>

            <button
              type="button"
              disabled={testingSmtp}
              onClick={handleTestSmtp}
              className="btn btn-secondary text-xs py-1.5 px-3"
              title="Send test verification email with Carbon Copy recipients"
            >
              <span>{testingSmtp ? '⏳' : '✉️'}</span>
              <span>{testingSmtp ? 'Sending...' : 'Test Email'}</span>
            </button>
          </div>
        </div>

        {/* Feedback Banners */}
        {error && (
          <div className="mt-2 rounded-lg bg-red-50 border border-red-200 px-4 py-2.5 text-xs text-red-700 flex items-center justify-between">
            <span>⚠️ {error}</span>
            <button type="button" onClick={() => setError('')} className="text-red-500 font-bold ml-2">✕</button>
          </div>
        )}
        {successMsg && (
          <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-700 flex items-center justify-between">
            <span>✅ {successMsg}</span>
            <button type="button" onClick={() => setSuccessMsg('')} className="text-emerald-600 font-bold ml-2">✕</button>
          </div>
        )}

        {/* Modal Body */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-5 pt-3 overflow-hidden">
            {/* Left Panel: Timing Rules & Variable Palette */}
            <div className="lg:col-span-5 flex flex-col min-h-0 overflow-y-auto pr-1 space-y-4">
              {/* Settings Card */}
              <div className="card p-4 space-y-3">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                  <span className="text-xs font-bold text-gray-900 uppercase tracking-wide">
                    Notification Rules
                  </span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentConfig.enabled}
                      onChange={(e) => updateCurrentConfig((p) => ({ ...p, enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-brand-600 focus:ring-brand-500 h-4 w-4"
                    />
                    <span className="text-xs font-semibold text-gray-700">Active</span>
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="label text-xs">Lead Notice</label>
                    <div className="relative">
                      <input
                        type="number"
                        min={1}
                        max={365}
                        value={currentConfig.lead_days}
                        onChange={(e) =>
                          updateCurrentConfig((p) => ({
                            ...p,
                            lead_days: parseInt(e.target.value, 10) || 30,
                          }))
                        }
                        className="input text-xs py-1.5"
                      />
                    </div>
                    <span className="text-[11px] text-gray-500 mt-1 block">Days before expiry</span>
                  </div>

                  <div>
                    <label className="label text-xs">Interval</label>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={currentConfig.interval_days}
                      onChange={(e) =>
                        updateCurrentConfig((p) => ({
                          ...p,
                          interval_days: parseInt(e.target.value, 10) || 7,
                        }))
                      }
                      className="input text-xs py-1.5"
                    />
                    <span className="text-[11px] text-gray-500 mt-1 block">Repeat every X days</span>
                  </div>

                  <div>
                    <label className="label text-xs">Max Reminders</label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={currentConfig.max_reminders}
                      onChange={(e) =>
                        updateCurrentConfig((p) => ({
                          ...p,
                          max_reminders: parseInt(e.target.value, 10) || 3,
                        }))
                      }
                      className="input text-xs py-1.5"
                    />
                    <span className="text-[11px] text-gray-500 mt-1 block">Limit per cert</span>
                  </div>
                </div>

                <EmailListInput
                  label="Carbon Copy (CC) Email List"
                  value={currentConfig.cc_emails}
                  onChange={(val) => updateCurrentConfig((p) => ({ ...p, cc_emails: val }))}
                  placeholder="e.g. safety@gngehs.com"
                  helperText="These addresses will automatically receive a Carbon Copy (CC) when renewal notices are sent."
                />
              </div>

              {/* Dynamic Variables Palette — Only visible when actively editing the template */}
              {activeView === 'editor' && isTemplateUnlocked && (
                <div className="card p-4 space-y-3 flex flex-col">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2.5">
                    <span className="text-xs font-bold text-gray-900 uppercase tracking-wide flex items-center gap-1.5">
                      <span>🏷️</span>
                      <span>Insert Dynamic Variables</span>
                    </span>
                    <span className="text-[11px] text-gray-600 bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200 font-medium">
                      Target:{' '}
                      <strong className="text-brand-700 font-semibold">
                        {focusedField === 'subject' ? 'Subject Line' : 'Message Body'}
                      </strong>
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-normal">
                    Click any variable below to insert it at your cursor location in the{' '}
                    <strong>{focusedField === 'subject' ? 'subject line' : 'message body'}</strong>:
                  </p>

                  <div className="space-y-3 overflow-y-auto max-h-[380px] pr-1">
                    {variableGroups.map((group) => (
                      <div key={group.category} className="space-y-1.5">
                        <span className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">
                          {group.category}
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {group.items.map((item) => (
                            <button
                              key={item.tag}
                              type="button"
                              onClick={() => handleInsertVariable(item.tag)}
                              title={`Click to insert ${item.tag}`}
                              className="text-xs px-2.5 py-1.5 rounded-lg bg-white hover:bg-brand-50 text-slate-800 hover:text-brand-700 border border-slate-200 hover:border-brand-400 shadow-2xs transition-all cursor-pointer inline-flex items-center gap-1.5 font-medium group"
                            >
                              <span className="text-brand-600 font-bold group-hover:scale-110 transition-transform">+</span>
                              <span>{item.label}</span>
                              <code className="text-[10px] text-slate-400 font-mono bg-slate-50 px-1 py-0.2 rounded border border-slate-100">
                                {item.tag}
                              </code>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel: Template Editor & Live Preview */}
            <div className="lg:col-span-7 flex flex-col min-h-0 overflow-hidden card">
              {/* Header inside right panel */}
              <div className="shrink-0 px-4 py-2.5 bg-slate-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {/* View Tabs */}
                  <div className="flex items-center gap-1 bg-gray-200/80 p-0.5 rounded-lg">
                    <button
                      type="button"
                      onClick={() => setActiveView('preview')}
                      className={`text-xs px-3 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                        activeView === 'preview'
                          ? 'bg-white text-gray-900 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      👁️ Live Preview
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isTemplateUnlocked) {
                          setShowConfirmEditModal(true);
                        } else {
                          setActiveView('editor');
                        }
                      }}
                      className={`text-xs px-3 py-1 rounded-md font-semibold transition-all cursor-pointer inline-flex items-center gap-1.5 ${
                        activeView === 'editor'
                          ? 'bg-white text-gray-900 shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <span>{isTemplateUnlocked ? '✏️' : '🔒'}</span>
                      <span>Edit Template</span>
                      {!isTemplateUnlocked && (
                        <span className="text-[10px] bg-slate-200/90 text-slate-600 font-medium px-1.5 py-0.2 rounded">
                          Locked
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Mode switcher: Normal Text vs Raw HTML & Lock Action */}
                  {activeView === 'editor' && isTemplateUnlocked && (
                    <div className="flex items-center gap-1 ml-2 pl-2 border-l border-gray-300">
                      <button
                        type="button"
                        onClick={() =>
                          updateCurrentConfig((p) => ({ ...p, editor_mode: 'text' }))
                        }
                        className={`text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer ${
                          currentConfig.editor_mode !== 'html'
                            ? 'bg-brand-50 text-brand-700 font-bold border border-brand-200'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title="Normal human-readable text editor (recommended)"
                      >
                        📝 Text Editor
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          updateCurrentConfig((p) => ({ ...p, editor_mode: 'html' }))
                        }
                        className={`text-xs px-2.5 py-1 rounded-md transition-colors cursor-pointer ${
                          currentConfig.editor_mode === 'html'
                            ? 'bg-brand-50 text-brand-700 font-bold border border-brand-200'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                        title="Raw HTML source code editor for developers"
                      >
                        💻 Raw HTML
                      </button>

                      <button
                        type="button"
                        onClick={() => setIsTemplateUnlocked(false)}
                        className="text-xs px-2 py-1 ml-1 text-slate-500 hover:text-slate-800 hover:bg-slate-200/70 rounded-md transition-colors inline-flex items-center gap-1 cursor-pointer font-medium"
                        title="Re-lock template to protect from accidental changes"
                      >
                        <span>🔒</span>
                        <span>Lock</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={saving}
                    onClick={handleSave}
                    className="btn btn-primary text-xs py-1.5 px-4"
                  >
                    <span>{saving ? '⏳' : '💾'}</span>
                    <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
                  </button>
                </div>
              </div>

              {/* Editor / Preview Content */}
              <div className="flex-1 min-h-0 overflow-hidden p-4 flex flex-col">
                {activeView === 'editor' ? (
                  !isTemplateUnlocked ? (
                    <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-slate-50/70 rounded-xl border border-dashed border-slate-300 m-2">
                      <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center text-2xl text-amber-700 mb-3 shadow-2xs">
                        🔒
                      </div>
                      <h4 className="text-base font-bold text-gray-900 mb-1">Email Template is Locked</h4>
                      <p className="text-xs text-gray-500 max-w-md mb-5 leading-relaxed">
                        To protect company renewal communications from accidental changes, the email subject and body template are protected by default.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowConfirmEditModal(true)}
                        className="btn btn-primary text-xs py-2 px-4 inline-flex items-center gap-2 shadow-sm font-medium"
                      >
                        <span>🔓</span>
                        <span>Unlock &amp; Edit Template</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 flex flex-col space-y-3 overflow-hidden">
                      {/* Subject Line */}
                      <div className="shrink-0">
                        <label className="label text-xs">Email Subject Line</label>
                        <input
                          ref={subjectRef}
                          type="text"
                          value={currentConfig.subject}
                          onFocus={() => setFocusedField('subject')}
                          onChange={(e) => updateCurrentConfig((p) => ({ ...p, subject: e.target.value }))}
                          placeholder="Subject line with {{variables}}..."
                          className="input text-xs py-2 font-medium"
                        />
                      </div>

                      {/* Message Body Field */}
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex items-center justify-between mb-1">
                          <label className="label text-xs">
                            {currentConfig.editor_mode === 'html'
                              ? 'Custom HTML Email Code'
                              : 'Email Message Body (Plain Text & Formatted Paragraphs)'}
                          </label>
                          <span className="text-[11px] text-gray-500">
                            {currentConfig.editor_mode === 'html'
                              ? 'Raw HTML template with styles'
                              : 'Type naturally. Paragraphs & bullet points are automatically formatted.'}
                          </span>
                        </div>

                        {currentConfig.editor_mode === 'html' ? (
                          <textarea
                            ref={htmlBodyRef}
                            value={currentConfig.body_html}
                            onFocus={() => setFocusedField('body')}
                            onChange={(e) => updateCurrentConfig((p) => ({ ...p, body_html: e.target.value }))}
                            placeholder="<html><body>...</body></html>"
                            className="input flex-1 min-h-0 font-mono text-xs p-3 leading-relaxed resize-none bg-slate-50"
                          />
                        ) : (
                          <textarea
                            ref={textBodyRef}
                            value={currentConfig.body_text || ''}
                            onFocus={() => setFocusedField('body')}
                            onChange={(e) => updateCurrentConfig((p) => ({ ...p, body_text: e.target.value }))}
                            placeholder="Type your message here... Use bullet points (•) and dynamic variables."
                            className="input flex-1 min-h-0 text-xs p-3 leading-relaxed resize-none"
                          />
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  /* Live Email Preview */
                  <div className="flex-1 flex flex-col min-h-0 overflow-hidden space-y-2">
                    <div className="shrink-0 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-xs text-gray-700 mb-1">
                        <span className="font-semibold text-gray-900">Subject: </span>
                        {renderPreviewSubject()}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        <span className="font-semibold text-gray-700">To: </span>
                        {MOCK_DATA_MAP[activeTab]['{{communication_email}}']}
                        {currentConfig.cc_emails && (
                          <span> &bull; <span className="font-semibold text-gray-700">CC: </span>{currentConfig.cc_emails}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-y-auto bg-gray-50 p-2">
                      <iframe
                        title="Live Rendered Email Preview"
                        srcDoc={renderPreviewBody()}
                        className="w-full h-full min-h-[420px] bg-white rounded border border-gray-200 shadow-xs"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Dialog to Unlock Template Editing */}
      <ConfirmDialog
        open={showConfirmEditModal}
        title="Edit Email Template?"
        message="This template defines the standard renewal notice sent to companies for this certificate type. Are you sure you want to unlock and edit it?"
        confirmLabel="Yes, Edit Template"
        cancelLabel="Cancel"
        variant="primary"
        onConfirm={() => {
          setIsTemplateUnlocked(true);
          setActiveView('editor');
          setShowConfirmEditModal(false);
        }}
        onCancel={() => setShowConfirmEditModal(false)}
      />
    </Modal>
  );
}
